import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Role, JobEstado, getJobEstadoName, getJobEstadoColor } from '../types/models';
import { workshopApi } from '../services/api';
import { Job, OperarioStatus, OperarioEstado, getOperarioEstadoName, getOperarioEstadoColor } from '../types/models';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Users,
  Wrench,
  AlertTriangle,
  CheckCircle,
  Car,
  UserCircle,
} from 'lucide-react';

export default function DashboardPage() {
  const { user, hasRole } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [operarioStatuses, setOperarioStatuses] = useState<OperarioStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [jobsData, statusesData] = await Promise.all([
        workshopApi.getJobs({}) as Promise<Job[]>,
        workshopApi.getOperarioStatuses() as Promise<OperarioStatus[]>,
      ]);
      setJobs(jobsData || []);
      setOperarioStatuses(statusesData || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const activeJobs = jobs.filter(j =>
    j.estado_id !== JobEstado.ENTREGADO &&
    j.estado_id !== JobEstado.CANCELADO
  );

  const urgentJobs = jobs.filter(j => j.priority_id === 20 || j.priority_id === 30);
  const completedToday = jobs.filter(j => j.estado_id === JobEstado.TERMINADO || j.estado_id === JobEstado.ENTREGADO);

  const operariosActivos = operarioStatuses.filter(s =>
    s.status_id !== OperarioEstado.FUERA && s.status_id !== OperarioEstado.LIBRE
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Bienvenido, {user?.first_name} {user?.last_name}
        </h1>
        <p className="text-gray-500">
          {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es })}
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Jobs Activos</p>
              <p className="text-3xl font-bold text-gray-800">{activeJobs.length}</p>
            </div>
            <Wrench className="h-10 w-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Urgentes</p>
              <p className="text-3xl font-bold text-gray-800">{urgentJobs.length}</p>
            </div>
            <AlertTriangle className="h-10 w-10 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Terminados Hoy</p>
              <p className="text-3xl font-bold text-gray-800">{completedToday.length}</p>
            </div>
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Operarios Activos</p>
              <p className="text-3xl font-bold text-gray-800">{operariosActivos.length}/{operarioStatuses.length}</p>
            </div>
            <Users className="h-10 w-10 text-purple-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Jobs Recientes */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Car className="h-5 w-5" />
              Jobs Activos
            </h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {activeJobs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No hay jobs activos
              </div>
            ) : (
              activeJobs.slice(0, 8).map((job) => (
                <div key={job.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800">
                        {job.vehicle?.placa || 'Sin placa'}
                      </span>
                      {job.priority_id === 20 && (
                        <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded-full">
                          Urgente
                        </span>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 text-xs text-white rounded ${getJobEstadoColor(job.estado_id as JobEstado)}`}>
                      {getJobEstadoName(job.estado_id as JobEstado)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{job.vehicle?.marca} {job.vehicle?.modelo}</span>
                    <span>{format(new Date(job.fecha_ingreso), 'dd MMM HH:mm')}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Estado de Operarios */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <UserCircle className="h-5 w-5" />
              Estado de Operarios
            </h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {operarioStatuses.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No hay operarios registrados
              </div>
            ) : (
              operarioStatuses.map((status) => (
                <div key={status.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">
                        {status.user?.first_name} {status.user?.last_name}
                      </span>
                      <span className={`w-3 h-3 rounded-full ${getOperarioEstadoColor(status.status_id as OperarioEstado)}`}></span>
                    </div>
                    <span className="text-sm text-gray-500">
                      {getOperarioEstadoName(status.status_id as OperarioEstado)}
                    </span>
                  </div>
                  {status.job && (
                    <div className="text-sm text-gray-500">
                      Trabajando en: {status.job.vehicle?.placa || 'Job'}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {hasRole([Role.OPERARIO]) && (
        <div className="mt-6 bg-white rounded-xl shadow-sm p-4">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Acciones Rápidas</h2>
          <div className="flex flex-wrap gap-3">
            <a
              href="/my-work"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Ver mi trabajo
            </a>
            <a
              href="/attendance"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              Marcar asistencia
            </a>
            <a
              href="/permissions"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Solicitar permiso
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
